import * as Sentry from '@sentry/react';
import { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  useLocation,
} from 'react-router-dom';
import GlowingBarLoadingIndicator from '../../components/LoadingIndicators/GlowingBarLoadingIndicator';
import useIsLoggedIn from '../../hooks/useIsLoggedIn';
import { colors } from '../../theme/colors';
import AccountSettingsPage from '../AccountSettingsPage';
import AppleCallbackPage from '../AppleCallbackPage';
import SignInCompletePage from '../LoginPage/SignInCompletePage';
import {
  DEFAULT_AUTHENTICATED_APP_ROUTE,
  LEGACY_MY_CIRCLE_ROUTE_PATH,
  ROUTE_PATH_NAMES,
} from './routePathNames';

const RootPage = lazy(() => import('../RootPage'));
const ChatPage = lazy(() => import('../ChatPage'));
const ChatActivitiesPage = lazy(() => import('../ChatActivitiesPage'));
const GroupChatDetailsPage = lazy(() => import('../GroupChatDetailsPage'));
const GroupChatInfoEditPage = lazy(() => import('../GroupChatInfoEditPage'));
const SocialActivityPage = lazy(() => import('../SocialActivityPage'));
const SocialActivityCreatePage = lazy(
  () => import('../SocialActivityCreatePage'),
);
const ActivityExpensesPage = lazy(() => import('../ActivityExpensesPage'));
const ActivityExpenseEditorPage = lazy(
  () => import('../ActivityExpenseEditorPage'),
);
const PlanningSharedTasksPage = lazy(
  () => import('../PlanningSharedTasksPage'),
);
const PlanningListSharePage = lazy(() => import('../PlanningListSharePage'));
const SocialChatPage = lazy(() => import('../SocialChatPage'));
const ChatsPage = lazy(() => import('../ChatsPage'));
const LoginPage = lazy(() => import('../LoginPage'));
const ErrorPage = lazy(() => import('../ErrorPage'));
const AssetPage = lazy(() => import('../AssetPage'));
const ProfilePage = lazy(() => import('../ProfilePages/ProfilePage'));
const CurrentUserProfilePage = lazy(
  () => import('../ProfilePages/CurrentUserProfilePage'),
);
const CreateAssetPage = lazy(() => import('../CreateUpdateAssetPage'));
const ExchangesHistoryPage = lazy(() => import('../ExchangesHistoryPage'));
const ExchangeAssetsPage = lazy(() => import('../ExchangeAssetsPage'));
const SearchAssetsPage = lazy(() => import('../SearchAssetsPage'));
const ResetPasswordPage = lazy(() => import('../ResetPasswordPage'));
const CurrentUserHubPage = lazy(() => import('../CurrentUserHubPage'));
const MyCirclePage = lazy(() => import('../MyCirclePage'));
const MyCircleNotesPage = lazy(() => import('../MyCircleNotesPage'));
const MyCircleCompletedTasksPage = lazy(
  () => import('../MyCircleCompletedTasksPage'),
);
const MyCircleListPage = lazy(() => import('../MyCircleListPage'));
const MyCircleTaskPage = lazy(() => import('../MyCircleTaskPage'));
const MyCircleTaskDetailPage = lazy(() => import('../MyCircleTaskDetailPage'));
const MyCircleActivitiesPage = lazy(() => import('../MyCircleActivitiesPage'));
const MyCircleActivitySectionPage = lazy(
  () => import('../MyCircleActivitySectionPage'),
);
const MyCircleAddByUsernamePage = lazy(
  () => import('../MyCircleAddByUsernamePage'),
);
const MyCircleContactRequestsPage = lazy(
  () => import('../MyCircleContactRequestsPage'),
);
const MyCircleGroupsPage = lazy(() => import('../MyCircleGroupsPage'));
const MyCircleNewGroupPage = lazy(() => import('../MyCircleNewGroupPage'));
const MyCircleNewGroupNamePage = lazy(
  () => import('../MyCircleNewGroupNamePage'),
);
const MyCircleNewMessagePage = lazy(() => import('../MyCircleNewMessagePage'));
const MyCircleSearchContactsPage = lazy(
  () => import('../MyCircleSearchContactsPage'),
);
const NotificationsPage = lazy(() => import('../NotificationsPage'));
const AssetWavesPage = lazy(() => import('../AssetWavesPage'));

const ClubsPage = lazy(() => import('../ClubsPage'));
const ClubActivityPage = lazy(() => import('../ClubActivityPage'));
const ClubActivityCreatePage = lazy(() => import('../ClubActivityCreatePage'));
const ClubActivityPhotosPage = lazy(() => import('../ClubActivityPhotosPage'));
const ClubDetailPage = lazy(() => import('../ClubsPage/ClubDetailPage'));
const AllClubsPage = lazy(() => import('../ClubsPage/AllClubsPage'));
const CreateClubIntroPage = lazy(
  () => import('../ClubsPage/CreateClubIntroPage'),
);
const CreateClubPage = lazy(() => import('../ClubsPage/CreateClubPage'));
const JoinClubPage = lazy(() => import('../ClubsPage/JoinClubPage'));
const ClubMembersPage = lazy(() => import('../ClubsPage/ClubMembersPage'));
const ClubSettingsPage = lazy(() => import('../ClubsPage/ClubSettingsPage'));
const InvitePage = lazy(() => import('../InvitePage'));
const DownloadPage = lazy(() => import('../DownloadPage'));

// Phone verification pages
const PhoneNumberEntryPage = lazy(
  () => import('../PhoneVerification/PhoneNumberEntry'),
);
const PhoneVerificationCodeEntryPage = lazy(
  () => import('../PhoneVerification/VerificationCodeEntry'),
);
const PhoneVerificationSuccessPage = lazy(
  () => import('../PhoneVerification/VerificationSuccess'),
);

// Email verification pages
const EmailVerificationPage = lazy(() => import('../EmailSignupPage'));

// Legal pages
const PrivacyPolicyPage = lazy(() => import('../PrivacyPolicyPage'));
const TermsOfConditionsPage = lazy(() => import('../TermsOfConditionsPage'));
const ChildSafetyPage = lazy(() => import('../ChildSafetyPage'));
const HelpCenterPage = lazy(() => import('../HelpCenterPage'));
const ScreenFallback = () => {
  return ReactDOM.createPortal(
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: colors.backgroundMediumLight,
        zIndex: 999999,
      }}
    >
      <GlowingBarLoadingIndicator />
    </div>,
    document.body,
  );
};
const AuthOnlyRoute = (props: any) => {
  const isLoggedIn = useIsLoggedIn();
  if (!isLoggedIn) {
    return <Navigate to={ROUTE_PATH_NAMES.LOGIN} />;
  }
  return props.children;
};

const NonAuthOnlyRoute = (props: any) => {
  const isLoggedIn = useIsLoggedIn();
  if (isLoggedIn) {
    return <Navigate to={DEFAULT_AUTHENTICATED_APP_ROUTE} />;
  }
  return props.children;
};

const LegacyMyCircleRouteRedirect = () => {
  const location = useLocation();
  const redirectPath = location.pathname.replace(
    LEGACY_MY_CIRCLE_ROUTE_PATH,
    ROUTE_PATH_NAMES.MY_CIRCLE,
  );

  return (
    <Navigate
      to={`${redirectPath}${location.search}${location.hash}`}
      replace
    />
  );
};

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

export const router = sentryCreateBrowserRouter(
  createRoutesFromElements([
    <Route
      path={ROUTE_PATH_NAMES.ROOT}
      element={
        <Suspense fallback={<ScreenFallback />}>
          <RootPage />
        </Suspense>
      }
      errorElement={
        <Suspense fallback={<ScreenFallback />}>
          <ErrorPage />
        </Suspense>
      }
    >
      <Route
        path={ROUTE_PATH_NAMES.ASSET_CREATE_UPDATE}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <CreateAssetPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.ASSET}
        element={
          <Suspense fallback={<ScreenFallback />}>
            <AssetPage />
          </Suspense>
        }
      />

      <Route
        path={ROUTE_PATH_NAMES.NOTIFICATIONS}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <NotificationsPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.ASSET_WAVES}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <AssetWavesPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.CLUBS}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <ClubsPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.CLUB_DETAIL}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <ClubDetailPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.CLUB_ACTIVITY_CREATE}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <ClubActivityCreatePage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.CLUB_ACTIVITY}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <ClubActivityPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.CLUB_ACTIVITY_PHOTOS}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <ClubActivityPhotosPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.CLUB_ALL}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <AllClubsPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.CLUB_CREATE_INTRO}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <CreateClubIntroPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.CLUB_CREATE}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <CreateClubPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.CLUB_JOIN}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <JoinClubPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.CLUB_MEMBERS}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <ClubMembersPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.CLUB_SETTINGS}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <ClubSettingsPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={LEGACY_MY_CIRCLE_ROUTE_PATH}
        element={<LegacyMyCircleRouteRedirect />}
      />
      <Route
        path={`${LEGACY_MY_CIRCLE_ROUTE_PATH}/*`}
        element={<LegacyMyCircleRouteRedirect />}
      />
      <Route
        path={ROUTE_PATH_NAMES.SEARCH}
        element={
          <Suspense fallback={<ScreenFallback />}>
            <SearchAssetsPage />
          </Suspense>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.MY_CIRCLE}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <MyCirclePage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.MY_CIRCLE_NOTES}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <MyCircleNotesPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.MY_CIRCLE_COMPLETED_TASKS}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <MyCircleCompletedTasksPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.MY_CIRCLE_LIST}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <MyCircleListPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.MY_CIRCLE_TASK}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <MyCircleTaskPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.MY_CIRCLE_TASK_DETAIL}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <MyCircleTaskDetailPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.MY_CIRCLE_ADD_BY_USERNAME}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <MyCircleAddByUsernamePage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.MY_CIRCLE_ACTIVITY_SECTION}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <MyCircleActivitySectionPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.MY_CIRCLE_ACTIVITIES}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <MyCircleActivitiesPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.MY_CIRCLE_CONTACT_REQUESTS}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <MyCircleContactRequestsPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.MY_CIRCLE_GROUPS}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <MyCircleGroupsPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.MY_CIRCLE_NEW_GROUP}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <MyCircleNewGroupPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.MY_CIRCLE_NEW_GROUP_NAME}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <MyCircleNewGroupNamePage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.MY_CIRCLE_NEW_MESSAGE}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <MyCircleNewMessagePage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.MY_CIRCLE_SEARCH_CONTACTS}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <MyCircleSearchContactsPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.HUB}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <CurrentUserHubPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.EXCHANGES_HISTORY}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <ExchangesHistoryPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.USER_PROFILE}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <ProfilePage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.CURRENT_USER_PROFILE}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <CurrentUserProfilePage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.ACCOUNT_SETTINGS}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <AccountSettingsPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
      <Route
        path={ROUTE_PATH_NAMES.EXCHANGE}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <ExchangeAssetsPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />

      <Route
        path={ROUTE_PATH_NAMES.CHAT}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <ChatPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />

      <Route
        path={ROUTE_PATH_NAMES.CHAT_ACTIVITIES}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <ChatActivitiesPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />

      <Route
        path={ROUTE_PATH_NAMES.GROUP_CHAT_DETAILS}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <GroupChatDetailsPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />

      <Route
        path={ROUTE_PATH_NAMES.GROUP_CHAT_INFO_EDIT}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <GroupChatInfoEditPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />

      <Route
        path={ROUTE_PATH_NAMES.SOCIAL_CHAT}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <SocialChatPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />

      <Route
        path={ROUTE_PATH_NAMES.SOCIAL_CHAT_PENDING}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <SocialChatPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />

      <Route
        path={ROUTE_PATH_NAMES.SOCIAL_ACTIVITY}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <SocialActivityPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />

      <Route
        path={ROUTE_PATH_NAMES.SOCIAL_ACTIVITY_CREATE}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <SocialActivityCreatePage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />

      <Route
        path={ROUTE_PATH_NAMES.ACTIVITY_EXPENSES}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <ActivityExpensesPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />

      <Route
        path={ROUTE_PATH_NAMES.ACTIVITY_EXPENSE_EDITOR}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <ActivityExpenseEditorPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />

      <Route
        path={ROUTE_PATH_NAMES.PLANNING_SHARED_TASKS}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <PlanningSharedTasksPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />

      <Route
        path={ROUTE_PATH_NAMES.PLANNING_LIST_SHARE}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <PlanningListSharePage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />

      <Route
        path={ROUTE_PATH_NAMES.CHATS}
        element={
          <AuthOnlyRoute>
            <Suspense fallback={<ScreenFallback />}>
              <ChatsPage />
            </Suspense>
          </AuthOnlyRoute>
        }
      />
    </Route>,
    <Route
      path={ROUTE_PATH_NAMES.LOGIN}
      element={
        <NonAuthOnlyRoute>
          <Suspense fallback={<ScreenFallback />}>
            <LoginPage />
          </Suspense>
        </NonAuthOnlyRoute>
      }
    />,
    <Route
      path={ROUTE_PATH_NAMES.RESET_PASSWORD}
      element={
        <NonAuthOnlyRoute>
          <Suspense fallback={<ScreenFallback />}>
            <ResetPasswordPage />
          </Suspense>
        </NonAuthOnlyRoute>
      }
    />,
    <Route
      path={ROUTE_PATH_NAMES.SIGN_IN_COMPLETE}
      element={
        <NonAuthOnlyRoute>
          <Suspense fallback={<ScreenFallback />}>
            <SignInCompletePage />
          </Suspense>
        </NonAuthOnlyRoute>
      }
    />,
    <Route
      path={ROUTE_PATH_NAMES.APPLE_CALLBACK}
      element={
        <Suspense fallback={<ScreenFallback />}>
          <AppleCallbackPage />
        </Suspense>
      }
    />,

    // Phone verification routes
    <Route
      path={ROUTE_PATH_NAMES.PHONE_VERIFICATION_ENTRY}
      element={
        <AuthOnlyRoute>
          <Suspense fallback={<ScreenFallback />}>
            <PhoneNumberEntryPage />
          </Suspense>
        </AuthOnlyRoute>
      }
    />,
    <Route
      path={ROUTE_PATH_NAMES.PHONE_VERIFICATION_CODE}
      element={
        <AuthOnlyRoute>
          <Suspense fallback={<ScreenFallback />}>
            <PhoneVerificationCodeEntryPage />
          </Suspense>
        </AuthOnlyRoute>
      }
    />,
    <Route
      path={ROUTE_PATH_NAMES.PHONE_VERIFICATION_SUCCESS}
      element={
        <AuthOnlyRoute>
          <Suspense fallback={<ScreenFallback />}>
            <PhoneVerificationSuccessPage />
          </Suspense>
        </AuthOnlyRoute>
      }
    />,

    <Route
      path={ROUTE_PATH_NAMES.EMAIL_SIGNUP}
      element={
        <Suspense fallback={<ScreenFallback />}>
          <EmailVerificationPage />
        </Suspense>
      }
    />,
    <Route
      path={ROUTE_PATH_NAMES.PRIVACY_POLICY}
      element={
        <Suspense fallback={<ScreenFallback />}>
          <PrivacyPolicyPage />
        </Suspense>
      }
    />,
    <Route
      path={ROUTE_PATH_NAMES.TERMS_OF_CONDITIONS}
      element={
        <Suspense fallback={<ScreenFallback />}>
          <TermsOfConditionsPage />
        </Suspense>
      }
    />,
    <Route
      path={ROUTE_PATH_NAMES.CHILD_SAFETY}
      element={
        <Suspense fallback={<ScreenFallback />}>
          <ChildSafetyPage />
        </Suspense>
      }
    />,
    <Route
      path={ROUTE_PATH_NAMES.HELP_CENTER}
      element={
        <Suspense fallback={<ScreenFallback />}>
          <HelpCenterPage />
        </Suspense>
      }
    />,
    <Route
      path={ROUTE_PATH_NAMES.INVITE}
      element={
        <Suspense fallback={<ScreenFallback />}>
          <InvitePage />
        </Suspense>
      }
    />,
    <Route
      path={ROUTE_PATH_NAMES.DOWNLOAD}
      element={
        <Suspense fallback={<ScreenFallback />}>
          <DownloadPage />
        </Suspense>
      }
    />,
  ]),
);
